import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AppComms,
  BYPASS_SPINNER,
  Cognito,
  DefaultOrgContext,
  DefaultStoreContext,
  EnvService,
  IFrameService,
  JwtService,
  ScopeOrgValue,
  Session,
  SessionToken,
  User,
  Visitor
} from '@enliven/angular';
import { BehaviorSubject, EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, first, map } from 'rxjs/operators';
import {
  ACTIVE_USER,
  E360_SESSION,
  MAX_IDLE_TIMEOUT_MINS,
  NEXT_ORG_E360_URL,
  ORG_DEFAULT_MODULE_ID,
  ORG_WEBSITE_BASE_URL,
  SESSION_TIMEOUT_MINS,
  SIGN_IN_URL,
  SSO_SIGN_OUT_MSG,
  SSO_USER_SIGNED_IN
} from '../constants/storage-constants';
import { SessionTransfer } from '../models/session-transfer.model';

@Injectable({
  providedIn: 'root'
})
export class E360SessionService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly iFrameService: IFrameService = inject(IFrameService);
  private readonly envService: EnvService = inject(EnvService);
  private readonly jwtService: JwtService = inject(JwtService);

  private readonly bypassSpinnerContext: HttpContext = new HttpContext().set(BYPASS_SPINNER, true);
  readonly SESSION_API = `${this.envService.sessionApiUrl}/v2/api/organizations`;

  private _organizationDefaultModuleId: string;
  private _organizationWebsiteBaseUrl: string;
  private _maxIdleTimeoutMinutes: number;
  private _sessionTimeoutMinutes: number;
  private _session: Session = {
    organizationId: undefined,
    organizationIdpExternalId: undefined,
    sessionId: undefined,
    sessiontokens: {
      idToken: '',
      accessToken: '',
      refreshToken: ''
    },
    visitor: undefined
  };

  appReload: boolean = false;

  readonly accessTokenExpirationMillisecondsSource: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  readonly accessTokenExpirationMilliseconds$ = this.accessTokenExpirationMillisecondsSource.asObservable();

  get session(): Session {
    return this._session;
  }

  get defaultOrganizationContext(): DefaultOrgContext {
    return this._session.defaultOrganizationContext;
  }

  get defaultStoreContext(): DefaultStoreContext {
    return this._session.defaultStoreContext;
  }

  get idToken(): string {
    return this._session.sessiontokens.idToken;
  }

  get accessToken(): string {
    return this._session.sessiontokens.accessToken;
  }

  get refreshToken(): string {
    return this._session.sessiontokens.refreshToken;
  }

  get visitor(): Visitor {
    return this._session.visitor;
  }

  set visitor(visitor: Visitor) {
    this._session.visitor = visitor;
    this.saveToStorage();
  }

  get isSSO(): boolean {
    return this._session.cognito.isSSO;
  }

  get orgId(): string {
    return this._session.organizationId;
  }

  set orgId(orgId: string) {
    this._session.organizationId = orgId;
  }

  get orgIdpExternalId(): string {
    return this._session.organizationIdpExternalId;
  }

  set orgIdpExternalId(val: string) {
    this._session.organizationIdpExternalId = val;
  }

  get organizationDefaultModuleId(): string {
    return this._organizationDefaultModuleId;
  }

  get organizationWebsiteBaseUrl(): string {
    return this._organizationWebsiteBaseUrl;
  }

  set organizationDefaultModuleId(id: string) {
    sessionStorage.setItem(ORG_DEFAULT_MODULE_ID, id);
    this._organizationDefaultModuleId = id;
  }

  set organizationWebsiteBaseUrl(id: string) {
    sessionStorage.setItem(ORG_WEBSITE_BASE_URL, id);
    this._organizationWebsiteBaseUrl = id;
  }

  get maxIdleTimeoutMinutes(): number {
    return this._maxIdleTimeoutMinutes;
  }

  set maxIdleTimeoutMinutes(mins: number) {
    sessionStorage.setItem(MAX_IDLE_TIMEOUT_MINS, mins.toString());
    this._maxIdleTimeoutMinutes = mins;
  }

  get sessionTimeoutMinutes(): number {
    return this._sessionTimeoutMinutes;
  }

  set sessionTimeoutMinutes(mins: number) {
    sessionStorage.setItem(SESSION_TIMEOUT_MINS, mins.toString());
    this._sessionTimeoutMinutes = mins;
  }

  appReloaded() {
    this.setSessionFromStorage();
    this.setAccessTokenExpiryTimer();
    this.appReload = true;
  }

  assignUserOrgIdIfNoAuthenticatingOrgId(userOrgId: string) {
    // E360 Session OrgId is assigned the value of the OrgID of the authenticating org during the fetch of cognito during app init
    // If there is no value (common platform user, URL omitted organizationId query param), then assign the authenticated user's OrgId value to the session
    if (this.orgId) return;
    this.orgId = userOrgId;
  }

  clearAccessTokenExpiryTimer() {
    this.accessTokenExpirationMillisecondsSource.next(null);
  }

  createE360Session(): Observable<Session> {
    if (this.appReload) return of(this._session);

    return this.postSessionToAPI();
  }

  createE360SessionTransfer(): Observable<SessionTransfer> {
    return this.http.post<SessionTransfer>(`${this.SESSION_API}/${this.orgId}/sessiontransfers`, this._session).pipe(
      map((sessionTransfer: SessionTransfer) => sessionTransfer),
      catchError(err => throwError(() => err))
    );
  }

  refreshSessionWithNewTokens(tokens: SessionToken) {
    this.setSessionTokens(tokens);
    this.setSession(this._session);
    this.setAccessTokenExpiryTimer();
    this.iFrameService.sendMessageToIFrame(AppComms.PASS_SESSION_DETAILS, this._session);
  }

  setCognitoInfo(cognito: Cognito) {
    this._session.cognito = cognito;
  }

  setDefaultStore(defaultStore: DefaultStoreContext) {
    if (defaultStore?.storeId) {
      this._session.defaultStoreContext = defaultStore;
    } else {
      delete this._session.defaultStoreContext;
    }
  }

  setDefaultOrg(defaultOrg: DefaultOrgContext) {
    if (defaultOrg?.organizationId) {
      this._session.defaultOrganizationContext = defaultOrg;
    } else {
      this._session.defaultOrganizationContext.organizationId = this._session.organizationId;
      this._session.defaultOrganizationContext.organizationIdType = 'organizationId';
    }
  }

  /**
   * Shared method to refresh tokens via HTTP POST to Cognito token endpoint
   * Used by both SSO (CognitoService) and Direct Login (AmplifyService)
   * @param refreshToken The refresh token to use for getting new tokens
   * @param userPoolUri The Cognito user pool URI
   * @param cognitoClientId The Cognito client ID
   * @returns Observable<SessionToken> with new id and access tokens
   */
  refreshTokensViaHttp(refreshToken: string, userPoolUri: string, cognitoClientId: string): Observable<SessionToken> {
    const headers = new HttpHeaders().set('content-type', 'application/x-www-form-urlencoded');
    const params = new URLSearchParams();
    params.set('grant_type', 'refresh_token');
    params.set('client_id', cognitoClientId);
    params.set('refresh_token', refreshToken);

    return this.http.post<any>(userPoolUri + '/oauth2/token', params.toString(), { headers }).pipe(
      map((response: any) => ({
        idToken: response.id_token,
        accessToken: response.access_token,
        refreshToken: undefined // Cognito doesn't return a new refresh token
      }))
    );
  }

  /**
   * Shared storage cleanup after successful sign-in
   * Used by both SSO and Direct Login authentication flows
   * @param isSSO Whether this is SSO (true) or Direct Login (false)
   */
  cleanupStorageAfterSignIn(isSSO: boolean) {
    sessionStorage.setItem(ACTIVE_USER, 'true');
    if (isSSO) {
      localStorage.setItem(SSO_USER_SIGNED_IN, 'true');
    } else {
      localStorage.removeItem(SSO_USER_SIGNED_IN);
    }
    localStorage.removeItem(SSO_SIGN_OUT_MSG);
    localStorage.removeItem(SIGN_IN_URL);
    localStorage.removeItem(NEXT_ORG_E360_URL);
  }

  setSessionTokens({ idToken, accessToken, refreshToken }: SessionToken) {
    this._session.sessiontokens.idToken = idToken;
    this._session.sessiontokens.accessToken = accessToken;
    if (refreshToken) {
      this._session.sessiontokens.refreshToken = refreshToken;
    }
  }

  setSession(session: Session) {
    this._session = session;
    this.saveToStorage();
  }

  setSessionFromStorage() {
    this._session = JSON.parse(sessionStorage.getItem(E360_SESSION));
  }

  saveToStorage() {
    sessionStorage.setItem(E360_SESSION, JSON.stringify(this._session));
  }

  private setAccessTokenExpiryTimer() {
    const decodedAccessToken: any = this.jwtService.decodeToken(this.accessToken);
    const expiryTimeInUnixMilliseconds: number = decodedAccessToken.exp * 1000;
    const currentTimeInUnixMilliseconds: number = Math.floor(new Date().getTime());
    const millisecondsUntilExpiry: number = Math.floor(expiryTimeInUnixMilliseconds - currentTimeInUnixMilliseconds);
    const oneMinuteBeforeExpiry: number = Math.floor(millisecondsUntilExpiry - 60000);
    this.accessTokenExpirationMillisecondsSource.next(oneMinuteBeforeExpiry);
  }

  updateE360Session(): Observable<Session> {
    return this.http
      .put<Session>(`${this.SESSION_API}/${this.orgId}/sessions/${this._session.sessionId}`, this._session, {
        context: this.bypassSpinnerContext
      })
      .pipe(
        map((sessionResponse: Session) => {
          this.setSession(sessionResponse);
          this.iFrameService.sendMessageToIFrame(AppComms.PASS_SESSION_DETAILS, this._session);
          return sessionResponse;
        }),
        catchError(err => throwError(() => err))
      );
  }

  clearSessionTokens(): void {
    if (this._session) {
      this._session.sessiontokens = { idToken: '', accessToken: '', refreshToken: '' };
    }
  }

  clearSessionDetails(): void {
    if (this._session) {
      this._session.defaultOrganizationContext = undefined;
      this._session.defaultStoreContext = undefined;
      this._session.sessionId = undefined;
      this._session.organizationId = undefined;
    }
  }

  getLastSavedSession(user: User): Observable<Session> {
    if (!user.savedSessionId) return of(null);

    const userOrgs: ScopeOrgValue[] = user.scopeRoles[0].scope.value?.organizations || [];

    // add user organization if no orgs returned for user scope
    if (!userOrgs?.length) {
      userOrgs.push({ id: user.organizationId });
    }

    return from(userOrgs).pipe(
      concatMap((org: { id: string }) =>
        this.http.get<Session>(`${this.SESSION_API}/${org.id}/sessions/${user.savedSessionId}`).pipe(
          catchError(() => EMPTY) // Skip failed requests, continue to next
        )
      ),
      first(), // Return first successful session
      // last saved session errors should not prevent the user from logging in
      defaultIfEmpty(null), // if none of the calls are successful return null
      catchError(() => of(null)) // if the observable errors out return null
    );
  }

  private postSessionToAPI(): Observable<Session> {
    return this.http.post<Session>(`${this.SESSION_API}/${this.orgId}/sessions`, this._session).pipe(
      map((sessionResponse: Session) => {
        this.setSession(sessionResponse);
        this.setAccessTokenExpiryTimer();
        return sessionResponse;
      }),
      catchError(err => throwError(() => err))
    );
  }
}
